import { TurnstileInstance } from '@marsidev/react-turnstile';
import { Stack, Typography, Link, Button, FormHelperText } from '@mui/material';
import React, { useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useLocation } from 'react-router-dom';

import LoadingButton from '@/@mantis/components/@extended/LoadingButton';
import { GoogleIcon } from '@/assets/icons/social/GoogleIcon';
import { PublicRoutes } from '@/config/routes';
import { useAuth } from '@/context/AuthProvider';
import { storePendingOAuthCampaign, clearPendingOAuthCampaign } from '@/localStorage/campaign';

import FormCaptcha from '@/components/form/FormCaptcha';
import FormCheckbox from '@/components/form/FormCheckbox';
import FormPassword from '@/components/form/FormPassword';
import FormTextField from '@/components/form/FormTextField';
import TermsOfServiceModal from '@/components/legal/TermsOfServiceModal';

import AuthPageWrapper from './AuthPageWrapper';

interface FormData {
  full_name: string;
  email: string;
  password: string;
  confirm_password: string;
  has_accepted_terms: boolean;
  captchaToken: string;
}

const SignUpPage: React.FC = () => {
  const { signUp, signInWithOAuth } = useAuth();
  const turnstileRef = useRef<TurnstileInstance>();
  const location = useLocation();

  // Extract all parameters from query
  const urlParams = new URLSearchParams(location.search);
  const campaign = urlParams.get('campaign') || 'property_owner';
  const redirect = urlParams.get('redirect');
  const referralType = urlParams.get('referral_type');
  const referralId = urlParams.get('referral_id');
  const referralUrl = urlParams.get('referral_url');

  // Build referral metadata from additional params
  const referralMetadata: Record<string, unknown> = {};
  urlParams.forEach((value, key) => {
    if (!['campaign', 'redirect', 'referral_type', 'referral_id', 'referral_url'].includes(key)) {
      referralMetadata[key] = value;
    }
  });

  // Store campaign in localStorage for use across pages
  localStorage.setItem('campaign', campaign);

  // Check if there's a shareRedirectUrl to preserve or if redirect was passed as param
  const [shareRedirectUrl] = useState(() => redirect || localStorage.getItem('shareRedirectUrl'));

  // If redirect is passed as parameter, store it in localStorage for later use
  React.useEffect(() => {
    if (redirect) {
      localStorage.setItem('shareRedirectUrl', redirect);
    }
  }, [redirect]);

  const {
    control,
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormData>();
  const [formError, setFormError] = useState<Error | null>(null);
  const [isTermsOfServiceModalOpen, setIsTermsOfServiceModalOpen] = useState(false);

  const onSignUpWithGoogle = async () => {
    setFormError(null);

    try {
      // Store campaign in localStorage before OAuth flow
      storePendingOAuthCampaign(campaign);

      // Store referral data in localStorage before OAuth flow
      if (referralType && referralUrl) {
        const referralData = {
          referralType,
          referralId,
          referralUrl,
          referralMetadata: {
            ...referralMetadata,
            campaign,
          },
        };
        localStorage.setItem('pendingReferral', JSON.stringify(referralData));
      }

      // Create campaign-aware redirect URL for email verification
      let redirectUrl = `${window.location.origin}/login?campaign=${campaign}`;
      if (shareRedirectUrl) {
        // Encode the shareRedirectUrl to pass it through OAuth flow
        redirectUrl += `&redirect=${encodeURIComponent(shareRedirectUrl)}`;
      }

      // Prepare user metadata including campaign
      const userMetadata: Record<string, unknown> = {
        campaign,
      };

      const { error } = await signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: redirectUrl,
          ...(Object.keys(userMetadata).length > 0 && { data: userMetadata }),
        },
      });

      if (error) throw error;
    } catch (error) {
      setFormError(error);
      // Clean up localStorage on error
      clearPendingOAuthCampaign();
    }
  };

  const onSignUpWithPassword = async (data: FormData) => {
    const { email, password, full_name, has_accepted_terms, captchaToken } = data;
    setFormError(null);

    try {
      const userMetadata: Record<string, unknown> = {
        full_name,
        has_accepted_terms,
        campaign,
      };

      // Create campaign-aware redirect URL for email verification
      // If there's a shareRedirectUrl, encode it in the redirect
      let emailRedirectUrl = `${window.location.origin}/login?campaign=${campaign}`;
      if (shareRedirectUrl) {
        // Encode the shareRedirectUrl to pass it through the email verification flow
        emailRedirectUrl += `&redirect=${encodeURIComponent(shareRedirectUrl)}`;
      }

      const response = await signUp({
        email,
        password,
        options: {
          emailRedirectTo: emailRedirectUrl,
          captchaToken,
          data: userMetadata,
        },
      });

      if (response.error) throw response.error;

      // Store referral data in localStorage to be processed after email verification
      if (referralType && referralUrl) {
        const referralData = {
          referralType,
          referralId,
          referralUrl,
          referralMetadata: {
            ...referralMetadata,
            campaign,
          },
        };
        localStorage.setItem('pendingReferral', JSON.stringify(referralData));
      }
    } catch (error) {
      setFormError(error);
      turnstileRef.current?.reset();
    }
  };

  return (
    <AuthPageWrapper>
      <Stack
        component="form"
        onSubmit={handleSubmit(onSignUpWithPassword)}
        spacing={3}
        width="100%"
      >
        <Stack spacing={1.5} mb={1}>
          <Typography variant="h3">Sign up</Typography>
        </Stack>

        <Stack spacing={2.5}>
          <FormTextField
            label="Name"
            placeholder="Enter your name"
            textFieldProps={{
              autoComplete: 'full_name',
              autoFocus: true,
            }}
            error={!!errors.full_name}
            helperText={errors.full_name?.message?.toString()}
            control={control}
            required
            {...register('full_name', {
              required: 'Name is required',
            })}
          />

          <FormTextField
            label="Email"
            placeholder="Enter your email"
            textFieldProps={{
              autoComplete: 'email',
              autoFocus: true,
              type: 'email',
            }}
            error={!!errors.email}
            helperText={errors.email?.message?.toString()}
            control={control}
            required
            {...register('email', {
              required: 'Email is required',
              pattern: {
                value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                message: 'Invalid email address',
              },
            })}
          />

          <FormPassword
            label="Password"
            placeholder="Create a password"
            error={!!errors.password}
            helperText={errors.password?.message?.toString() ?? 'Must be at least 8 characters.'}
            control={control}
            required
            {...register('password', {
              required: 'Password is required',
              minLength: {
                value: 8,
                message: 'Password must be at least 8 characters',
              },
            })}
          />

          <FormPassword
            label="Confirm Password"
            placeholder="Confirm your password"
            error={!!errors.confirm_password}
            helperText={errors.confirm_password?.message?.toString()}
            control={control}
            required
            {...register('confirm_password', {
              required: 'Confirm Password is required',
              validate: (value, data) => value === data.password || 'Passwords do not match',
            })}
          />

          <FormCheckbox
            controlLabel={
              <>
                I agree to the{' '}
                <Button
                  onClick={() => setIsTermsOfServiceModalOpen(true)}
                  sx={{
                    p: 0,
                    minWidth: 0,
                    textTransform: 'none',
                    color: 'primary.main',
                    '&:hover': {
                      bgcolor: 'transparent',
                      textDecoration: 'underline',
                    },
                  }}
                >
                  Terms and Conditions.
                </Button>
              </>
            }
            control={control}
            required
            {...register('has_accepted_terms', {
              required: 'You must agree to the terms and conditions',
            })}
            error={!!errors.has_accepted_terms}
            helperText={errors.has_accepted_terms?.message?.toString()}
          />

          <FormCaptcha
            control={control}
            error={!!errors.captchaToken}
            helperText={errors.captchaToken?.message?.toString()}
            required
            {...register('captchaToken', {
              required: 'Captcha Token is required',
            })}
            turnstileRef={turnstileRef}
          />

          {!!formError && <FormHelperText error>{formError.message}</FormHelperText>}
        </Stack>

        <Stack spacing={1} mb={1}>
          <LoadingButton loading={isSubmitting} fullWidth variant="contained" type="submit">
            Get Started
          </LoadingButton>

          <Button
            fullWidth
            color="secondary"
            variant="outlined"
            onClick={onSignUpWithGoogle}
            startIcon={<GoogleIcon />}
          >
            Sign up with Google
          </Button>
        </Stack>

        <Typography variant="body3" textAlign="center">
          Already have an account?{' '}
          <Link href={(() => {
            const loginParams = new URLSearchParams();
            if (campaign) loginParams.append('campaign', campaign);
            if (redirect) loginParams.append('redirect', redirect);
            if (referralType) loginParams.append('referral_type', referralType);
            if (referralId) loginParams.append('referral_id', referralId);
            if (referralUrl) loginParams.append('referral_url', referralUrl);
            
            const paramString = loginParams.toString();
            return `${PublicRoutes.login}${paramString ? `?${paramString}` : ''}`;
          })()}>
            Log in
          </Link>
        </Typography>
      </Stack>

      {isTermsOfServiceModalOpen && (
        <TermsOfServiceModal onClose={() => setIsTermsOfServiceModalOpen(false)} />
      )}
    </AuthPageWrapper>
  );
};

export default SignUpPage;
